// Sort options for the recordings lists, mirroring the four the hikes lists offer
// (see HikeFilterBar.vue / useHikeFilters.ts) so recordings sort the same way on
// web, iOS and Android.
//
// The wire values match Queries::Concerns::RecordingSortOrder on the backend.
// Recordings are titled rather than named, so the alphabetical value is
// `title_asc` where hikes use `name_asc`.
//
// Sorting is applied server-side. It has to be: the list pages in 20 at a time,
// so re-ordering only the pages already loaded (which is what this screen used to
// do) puts the true longest recording at the bottom until every page is fetched.

export const RECORDING_SORT_DEFAULT = 'created_desc'

export interface RecordingSortOption {
  value: string
  label: string
}

export const RECORDING_SORT_OPTIONS: readonly RecordingSortOption[] = Object.freeze([
  { value: 'created_desc', label: 'Recorded date (newest first)' },
  { value: 'title_asc', label: 'Title (A–Z)' },
  { value: 'distance_desc', label: 'Distance (longest first)' },
  { value: 'distance_asc', label: 'Distance (shortest first)' },
])

/** Whether the sort chip should render as active (i.e. the user changed it). */
export function isRecordingSortActive (sort: string | null | undefined): boolean {
  return normalizeRecordingSort(sort) !== RECORDING_SORT_DEFAULT
}

/**
 * Coerces any stored/queried value to one this backend understands. A value from
 * an older bookmarked URL (e.g. the `created_asc` this screen used to offer
 * client-side) has no server equivalent, so it degrades to the default rather
 * than being sent through and silently ignored.
 */
export function normalizeRecordingSort (sort: string | null | undefined): string {
  if (!sort) return RECORDING_SORT_DEFAULT
  const known = RECORDING_SORT_OPTIONS.some((option) => option.value === sort)
  return known ? sort : RECORDING_SORT_DEFAULT
}

/** The label for the currently applied sort, for the chip / summary text. */
export function recordingSortLabel (sort: string | null | undefined): string {
  const value = normalizeRecordingSort(sort)
  const option = RECORDING_SORT_OPTIONS.find((o) => o.value === value)
  return option ? option.label : RECORDING_SORT_OPTIONS[0].label
}
